import { hole } from './api'

/**
 * Die sechs Auswertungsbildschirme. **Alle nur lesend** — sie zeigen an, sie
 * greifen nicht ein. Wo etwas zu tun ist, führen sie in die Warteschlangen.
 */

// --- 1. Prüfstand --------------------------------------------------------

export interface Pruefung {
  pruefung: string
  ist: number
  herkunft: string
  urteil: 'ok' | 'PRUEFEN'
}

export const holePruefstand = () =>
  hole<{ pruefungen: Pruefung[]; dauerMs: number }>('/api/kennzahlen/pruefstand')

export const holePunkteabgleich = () =>
  hole<{ abweichungen: number; dauerMs: number }>('/api/kennzahlen/punkteabgleich')

// --- 2. Journal ----------------------------------------------------------

export interface Journaleintrag {
  decided_at: string
  target_kind: string
  target_id: string
  domain: string | null
  decision: string
  reason: string | null
  was_sampled: boolean
  entschieden_von: string | null
  betrifft: string | null
  betrifft_uid: string | null
}

export const holeJournal = (f: {
  zielArt?: string
  entscheidung?: string
  seit?: string
}) => {
  const p = new URLSearchParams()
  if (f.zielArt) p.set('zielArt', f.zielArt)
  if (f.entscheidung) p.set('entscheidung', f.entscheidung)
  if (f.seit) p.set('seit', f.seit)
  const anhang = p.toString()
  return hole<{ eintraege: Journaleintrag[]; dauerMs: number }>(
    `/api/kennzahlen/journal${anhang ? `?${anhang}` : ''}`,
  )
}

// --- 5. Dubletten --------------------------------------------------------

export interface Dublettenpaar {
  id_a: string
  name_a: string | null
  strasse_a: string | null
  stadt_a: string | null
  quelle_a: string | null
  bilder_a: number
  id_b: string
  name_b: string | null
  strasse_b: string | null
  stadt_b: string | null
  quelle_b: string | null
  bilder_b: number
  meter: number
  namensaehnlichkeit: number
}

export const holeDubletten = (umkreis: number, aehnlichkeit: number) =>
  hole<{
    paare: Dublettenpaar[]
    dauerMs: number
    umkreis: number
    aehnlichkeit: number
  }>(`/api/kennzahlen/dubletten?umkreis=${umkreis}&aehnlichkeit=${aehnlichkeit}`)

// --- 6. Herkunft ---------------------------------------------------------

export interface Herkunftszeile {
  origin: string
  license: string | null
  felder: number
  orte: number
  felder_namentlich: string[]
}

export interface GoogleFeld {
  id: string
  name: string | null
  city: string | null
  field_path: string
  license: string | null
  acquired_at: string
}

export const holeHerkunft = () =>
  hole<{ zeilen: Herkunftszeile[]; dauerMs: number }>('/api/kennzahlen/herkunft')

export const holeGoogleFelder = () =>
  hole<{ felder: GoogleFeld[]; auffaellig: GoogleFeld[]; dauerMs: number }>(
    '/api/kennzahlen/herkunft/google',
  )

/** Die Herkunftsangaben im Klartext. */
export const HERKUNFT_TEXT: Record<string, string> = {
  osm: 'OpenStreetMap',
  google: 'Google Maps',
  editorial: 'eigene Redaktion',
  user: 'Nutzereinreichung',
}

export const ZIEL_TEXT: Record<string, string> = {
  media: 'Bild',
  review: 'Bewertung',
  submission: 'Einreichung',
  report: 'Meldung',
  place: 'Ort',
}

export const ENTSCHEIDUNG_TEXT: Record<string, string> = {
  accepted: 'angenommen',
  rejected: 'abgelehnt',
  reverted: 'zurückgenommen',
  auto_approved: 'automatisch freigegeben',
}

// --- Kanäle --------------------------------------------------------------

/**
 * Über welchen Kanal neue Konten kommen. Die Zahlen stammen fertig aus zwei
 * Views (Migration 073 im Cloud-Repo) — hier wird nichts nachgerechnet.
 */
export type Kanal = 'app-1.0' | 'ios' | 'web' | 'unbekannt'

export interface KanalWoche {
  /** Montag der Kalenderwoche, `YYYY-MM-DD`. */
  woche: string
  kanal: Kanal
  neue_konten: number
  davon_echt: number
  davon_mit_beitrag: number
  davon_wechsler: number
}

export interface KanalAktiv {
  kanal: 'ios' | 'web'
  aktiv_7_tage: number
  aktiv_30_tage: number
  konten_mit_geraet: number
}

/** Je Kanal über die ganze Lebenszeit, unabhängig vom Zeitraum. */
export type KanalGesamt = Omit<KanalWoche, 'woche'>

/** 12, 26 oder 52 Wochen — `null` ist „alles". */
export type Zeitraum = 12 | 26 | 52 | null

export const holeKanaele = (zeitraum: Zeitraum) =>
  hole<{
    zeitraum: Zeitraum
    wochen: KanalWoche[]
    aktiv: KanalAktiv[]
    gesamt: KanalGesamt[]
    dieseWoche: string
  }>(`/api/kennzahlen/kanaele?wochen=${zeitraum ?? 'alles'}`)
